/**
 * @fileoverview Word-style horizontal ruler for the template editor.
 *
 * Sits directly above the A4 page and shows the page width in the user's
 * chosen margin unit (cm / px). The two triangular markers are the BODY
 * left and right margins; dragging them writes `bodyLeftPadding` /
 * `bodyRightPadding` overrides exactly as typing into the Margins popover
 * would, so the ruler and the popover always agree. The shaded ends are
 * the margin regions, the white run between them is the text column.
 *
 * Header / footer margins are not on the ruler (Word doesn't put them there
 * either) — use the Margins popover for those.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@/utils/misc";
import {
  MARGIN_LIMITS,
  formatForUnit,
  pxToCm,
  type MarginUnit,
} from "@/utils/branding/marginUnits";

const PX_PER_CM = 96 / 2.54;

/** Minimum text column Word will let you drag down to (px). */
const MIN_COLUMN_PX = 96;

export interface EditorRulerProps {
  /** Page width in CSS px (A4 @ 96 DPI = 794). */
  pageWidthPx: number;
  /** Current effective body margins (px). */
  leftPx: number;
  rightPx: number;
  unit: MarginUnit;
  /** Fired continuously while dragging; values are integer px. */
  onChange: (next: { leftPx: number; rightPx: number }) => void;
  /** Fired once when a drag ends — the commit point for autosave. */
  onCommit?: () => void;
  /** Render as read-only (markers not draggable). */
  disabled?: boolean;
  className?: string;
}

export function EditorRuler({
  pageWidthPx,
  leftPx,
  rightPx,
  unit,
  onChange,
  onCommit,
  disabled,
  className,
}: EditorRulerProps) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [dragging, setDragging] = useState<"left" | "right" | null>(null);
  // Live values during a drag so the readout tracks the pointer even if the
  // parent debounces its state update.
  const liveRef = useRef({ leftPx, rightPx });
  useEffect(() => {
    if (!dragging) liveRef.current = { leftPx, rightPx };
  }, [leftPx, rightPx, dragging]);

  const clampPair = useCallback(
    (which: "left" | "right", value: number) => {
      const max = MARGIN_LIMITS.px.max;
      const other =
        which === "left" ? liveRef.current.rightPx : liveRef.current.leftPx;
      const limit = Math.min(max, pageWidthPx - other - MIN_COLUMN_PX);
      return Math.max(0, Math.min(limit, Math.round(value)));
    },
    [pageWidthPx],
  );

  const beginDrag = (which: "left" | "right") => (e: React.PointerEvent) => {
    if (disabled) return;
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    setDragging(which);
  };

  const moveDrag = (e: React.PointerEvent) => {
    if (!dragging || !trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const scale = rect.width / pageWidthPx;
    const x = (e.clientX - rect.left) / scale;
    const next =
      dragging === "left"
        ? { leftPx: clampPair("left", x), rightPx: liveRef.current.rightPx }
        : {
            leftPx: liveRef.current.leftPx,
            rightPx: clampPair("right", pageWidthPx - x),
          };
    if (
      next.leftPx === liveRef.current.leftPx &&
      next.rightPx === liveRef.current.rightPx
    ) {
      return;
    }
    liveRef.current = next;
    onChange(next);
  };

  const endDrag = (e: React.PointerEvent) => {
    if (!dragging) return;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      // Pointer capture may already be released (e.g. pointercancel).
    }
    setDragging(null);
    onCommit?.();
  };

  // Tick layout. cm: minor every 0.25cm, medium every 0.5cm, labelled every
  // 1cm — Word's default metric ruler. px: minor every 10px, medium 50px,
  // labelled 100px.
  const ticks: Array<{
    x: number;
    kind: "minor" | "medium" | "major";
    label?: string;
  }> = [];
  if (unit === "cm") {
    const totalCm = pxToCm(pageWidthPx);
    for (let i = 0; i * 0.25 <= totalCm; i++) {
      const cm = i * 0.25;
      const x = cm * PX_PER_CM;
      if (i % 4 === 0)
        ticks.push({ x, kind: "major", label: cm === 0 ? "" : String(cm) });
      else if (i % 2 === 0) ticks.push({ x, kind: "medium" });
      else ticks.push({ x, kind: "minor" });
    }
  } else {
    for (let x = 0; x <= pageWidthPx; x += 10) {
      if (x % 100 === 0)
        ticks.push({ x, kind: "major", label: x === 0 ? "" : String(x) });
      else if (x % 50 === 0) ticks.push({ x, kind: "medium" });
      else ticks.push({ x, kind: "minor" });
    }
  }

  const pct = (px: number) => `${(px / pageWidthPx) * 100}%`;
  const readout =
    dragging === "left"
      ? `Left ${formatForUnit(liveRef.current.leftPx, unit)} ${unit}`
      : dragging === "right"
        ? `Right ${formatForUnit(liveRef.current.rightPx, unit)} ${unit}`
        : null;

  return (
    <div
      className={cn("select-none", className)}
      style={{ width: pageWidthPx }}
      data-testid="editor-ruler"
    >
      <div
        ref={trackRef}
        className="relative h-6 rounded-sm border border-border/60 bg-white shadow-sm dark:bg-white"
        onPointerMove={moveDrag}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        {/* Margin regions */}
        <div
          className="absolute inset-y-0 left-0 bg-primary/[0.08]"
          style={{ width: pct(leftPx) }}
        />
        <div
          className="absolute inset-y-0 right-0 bg-primary/[0.08]"
          style={{ width: pct(rightPx) }}
        />

        {/* Ticks */}
        {ticks.map((t, i) => (
          <div
            key={i}
            className={cn(
              "absolute bottom-0 w-px bg-primary/40",
              t.kind === "major" ? "h-3" : t.kind === "medium" ? "h-2" : "h-1",
            )}
            style={{ left: pct(t.x) }}
          >
            {t.label && (
              <span className="absolute -top-3 left-0 -translate-x-1/2 text-[9px] tabular-nums text-primary/55">
                {t.label}
              </span>
            )}
          </div>
        ))}

        {/* Markers — a downward triangle on the top edge, like Word's
            indent/margin markers. */}
        <RulerMarker
          side="left"
          x={pct(leftPx)}
          active={dragging === "left"}
          disabled={disabled}
          onPointerDown={beginDrag("left")}
          title="Drag to set the body's left margin"
        />
        <RulerMarker
          side="right"
          x={pct(pageWidthPx - rightPx)}
          active={dragging === "right"}
          disabled={disabled}
          onPointerDown={beginDrag("right")}
          title="Drag to set the body's right margin"
        />

        {readout && (
          <div className="pointer-events-none absolute -top-7 left-1/2 -translate-x-1/2 rounded bg-primary px-1.5 py-0.5 text-[10px] font-medium text-primary-foreground shadow">
            {readout}
          </div>
        )}
      </div>
    </div>
  );
}

function RulerMarker({
  side,
  x,
  active,
  disabled,
  onPointerDown,
  title,
}: {
  side: "left" | "right";
  x: string;
  active: boolean;
  disabled?: boolean;
  onPointerDown: (e: React.PointerEvent) => void;
  title: string;
}) {
  return (
    <button
      type="button"
      aria-label={title}
      title={title}
      data-testid={`editor-ruler-marker-${side}`}
      disabled={disabled}
      onPointerDown={onPointerDown}
      className={cn(
        "absolute top-0 z-10 h-full w-3 -translate-x-1/2 touch-none bg-transparent",
        disabled ? "cursor-default" : "cursor-ew-resize",
      )}
      style={{ left: x }}
    >
      <span
        className={cn(
          "absolute left-1/2 top-0 block size-0 -translate-x-1/2 border-x-[5px] border-t-[7px] border-x-transparent",
          active ? "border-t-primary" : "border-t-primary/70",
        )}
      />
      <span
        className={cn(
          "absolute bottom-0 left-1/2 block h-3 w-px -translate-x-1/2",
          active ? "bg-primary" : "bg-primary/50",
        )}
      />
    </button>
  );
}
